import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertItem, AlertType, Company, Region } from 'src/app/core/models';
import { City } from 'src/app/core/models/City';
import { State } from 'src/app/core/models/State';
import { AlertService, CompanyService, ModalService, RegionService } from 'src/app/core/services';
import { CityService } from 'src/app/core/services/city.service';
import { Gb } from 'src/app/core/services/growthbook.service';
import { StateService } from 'src/app/core/services/state.service';
import { BaseModal } from 'src/app/core/utils/BaseModal';

@Component({
  selector: 'app-create-or-update-company',
  templateUrl: './create-or-update-company.component.html',
  styleUrls: ['./create-or-update-company.component.sass']
})
export class CreateOrUpdateCompanyComponent extends BaseModal implements OnInit {

  public loading = false;
  public isLoading = false;
  public loadingPromise = false;
  public isValid = false;
  public lastState: any;
  public editMode = false;
  public companyId = 0;
  public company: Company;
  public form: FormGroup;
  public regions: Region[] = [];
  public states: State[] = [];
  public cities: City[] = [];
  public enabledCompany = true;
  public logoFile = [];
  public existCompany = false;
  public findCompany = false;
  public gb: Gb;

  constructor(
    modalService: ModalService,
    private companyService: CompanyService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private regionService: RegionService,
    private stateService: StateService,
    private cityService: CityService
  ) {
    super(modalService);
  }

  async ngOnInit() {
    this.gb = new Gb();
    this.loading = true;
    this.editMode = this.initialState?.editMode;
    this.company = this.initialState?.company;
    this.companyId = this.initialState?.company?.id;
    await this.getRegions();
    if (this.editMode) {
      await this.getStates();
      await this.getCities();
      if (this.company.logo !== null) {
        this.logoFile.push(this.loadFileDetails(this.company.logo));
      }
      if (this.gb.isOn('ECS-3736')) {
        if (this.logoFile[0].data === '') {
          this.logoFile = [];
        }
      }
    }
    this.createForm();
    await this.setDefaultRegion();
    this.loading = false;
  }

  async getCompany() {
    await this.companyService.getById(this.companyId).then(res => {
      this.company = res;
    }).catch(err => {
      console.error(err);
    });
  }

  async enabledOrDisabledCompany(company: Company) {
    if (company.cnpj === null) {
      this.alertService.show(new AlertItem('CompanyRequiredFields', AlertType.warning, false, 5000));
      return;
    }
    if (this.editMode) {
      company.enabled = !company.enabled;
      this.enabledCompany = company.enabled;
      const data: Company = Company.parse({
        id: company.id,
        name: company.name,
        cnpj: company.cnpj,
        enabled: company.enabled,
        regionId: company.regionId,
        cityId: company.cityId,
        address: company.address,
        logo: company.logo?.split('.com/')[1]?.split('?')[0] !== 'undefined' ? company.logo : undefined
      });
      await this.companyService.update(data).then(res => {
        this.alertService.show(new AlertItem('UpdateCompanySuccess', AlertType.success));
      }).catch(err => {
        this.alertService.show(new AlertItem('UpdateCompanyError', AlertType.danger));
      });
    } else {
      this.enabledCompany = !this.enabledCompany;
    }
  }

  async getRegions() {
    await this.regionService.list({}).then(async res => {
      this.regions = res.result.sort();
    });
  }

  async setDefaultRegion() {
    const defaultRegion = this.regions.find(region => region.name === 'Brasil');
    if (defaultRegion !== undefined) {
      this.form.get('regionId').setValue(defaultRegion.id);
      await this.getStatesOnValuesChange();
    }
  }

  async onAllSubmited(): Promise<any> {
    this.loadingPromise = true;
    const data: Company = Company.create({
      name: this.form.value.name,
      cnpj: this.form.value.cnpj.replaceAll('.', '').replaceAll('/', '').replaceAll('-', ''),
      regionId: this.form.value.regionId,
      cityId: Number(this.form.value.cityId),
      enabled: this.enabledCompany,
      address: {
        street: this.form.value.street,
        number: this.form.value.number,
        zipcode: this.form.value.zipcode,
        neighborhood: this.form.value.neighborhood,
      }
    });
    if (this.company?.Address?.id) {
      data.addressId = Number(this.company?.Address?.id);
    }
    const searchEvent = new CustomEvent('searchEvent', {
      detail: { search: true }
    });
    if (!this.editMode) {
      data.logo = (this.form.get('logo').value || []).map(f => f.isNew === true ? f.detail : f.data)[0];
      await this.companyService.create(data, true).then(res => {
        this.alertService.show(new AlertItem('CreateCompanySuccess', AlertType.success, false, 5000));
        document.dispatchEvent(searchEvent);
        this.onClose();
      }).catch(err => {
        this.alertService.show(new AlertItem('CreateCompanyError', AlertType.danger, false, 5000));
      });
    } else {
      data.id = this.company.id;
      data.logo = (this.form.get('logo').value || []).map(f => f.isNew === true ? f.detail : f.data)[0];
      if (data.logo === undefined) {
        data.logo = this.company.logo?.split('.com/')[1]?.split('?')[0];
      }
      if (this.gb.isOn('ECS-3736')) {
        if (this.logoFile.length === 0) {
          data.logo = '';
        }
      }
      await this.companyService.update(data, true).then(res => {
        this.alertService.show(new AlertItem('UpdateCompanySuccess', AlertType.success, false, 5000));
        document.dispatchEvent(searchEvent);
        this.onClose();
      }).catch(err => {
        this.alertService.show(new AlertItem('UpdateCompanyError', AlertType.danger, false, 5000));
      });
    }
    this.loadingPromise = false;
  }

  onSubmit(): void {
    this.onAllSubmited();
  }

  createForm() {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      cnpj: ['', Validators.required],
      street: [''],
      number: [''],
      zipcode: [''],
      regionId: [''],
      stateId: [[]],
      cityId: [[]],
      neighborhood: [''],
      logo: [''],
    });
    this.form.get('name').valueChanges.subscribe(async value => {
      this.findCompany = true;
      setTimeout(async () => {
        await this.companyService.list({ 'name[like]': `[%${value}%]` }).then(res => {
          if (res.result.length > 0) {
            if (value === res.result[0].name && value !== this.company?.name) {
              this.alertService.show(new AlertItem('CompanyExists', AlertType.warning, false, 10000));
              this.form.get('name')?.setErrors({ invalid: true });
              this.existCompany = true;
            }
          } else {
            this.existCompany = false;
          }
          this.findCompany = false;
        }).catch(err => {
          this.findCompany = false;
        });
      }, 2500);
    });
    this.form.get('cnpj').valueChanges.subscribe(value => {
      if (value.length < 14 || value.length > 14) {
        this.form.get('cnpj')?.setErrors({ invalid: true });
      }
    });
    this.getStatesOnValuesChange();
    this.form.get('stateId').valueChanges.subscribe(async value => {
      this.isLoading = true;
      await this.cityService.list({
        regionId: this.form.value.regionId,
        'stateId[in]': `[${value}]`,
        order: 'name'
      }).then(res => {
        this.cities = res.result.sort((a, b): any => Number(a.name) - Number(b.name));
      }).catch(err => {
        console.error(err);
        this.loading = false;
      });
      this.isLoading = false;
    });
    if (this.editMode) {
      this.form.get('name').patchValue(this.company.name || '');
      this.form.get('cnpj').patchValue(this.company.cnpj || '');
      this.form.get('street').patchValue(this.company?.Address?.street || '');
      this.form.get('number').patchValue(this.company?.Address?.number || '');
      this.form.get('zipcode').patchValue(this.company?.Address?.zipcode || '');
      this.form.get('regionId').patchValue(this.company.regionId || '');
      this.form.get('stateId').patchValue(this.company?.City?.State?.id || '');
      this.form.get('cityId').patchValue(this.company?.City?.id || '');
      this.form.get('neighborhood').patchValue(this.company?.Address?.neighborhood || '');
      this.enabledCompany = this.company.enabled;
    }
  }

  async getStatesOnValuesChange() {
    this.form.get('regionId').valueChanges.subscribe(async value => {
      this.isLoading = true;
      await this.stateService.list({
        regionId: value,
        order: 'name'
      }).then(res => {
        this.states = res.result.sort();
        if (value !== this.company.regionId) {
          this.cities = [];
        } else {
          this.form.get('stateId').patchValue(this.company?.City?.State?.id || '');
          this.form.get('cityId').patchValue(this.company?.City?.id || '');
        }
      });
      this.isLoading = false;
    });
  }

  loadFileDetails(url) {
    const name = this.getFileName(url);
    const type = this.getFileType(name);
    const data = {
      detail: {
        type,
        name
      },
      data: url
    };
    return data;
  }

  getFileType(name) {
    let type = name.split('.').pop();
    switch (type) {
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        type = `image/${type}`;
        break;
      default:
        return type;
    }
    return type;
  }

  getFileName(src) {
    const decoded = decodeURIComponent(src || '');
    const result = decoded.match(/(metrologic#[0-9]+\/)([^\?]+)/) || '';
    return result !== '' ? result[2] : result;
  }

  async getStates() {
    if (this.company.regionId !== null) {
      await this.stateService.list({
        regionId: `${this.company.regionId}`,
        order: 'name'
      }).then(res => {
        this.states = res.result.sort();
      });
    }
  }

  async getCities() {
    if (this.company.City && this.company.City.State.id) {
      await this.cityService.list({
        regionId: `${this.company.regionId}`,
        'stateId[in]': `[${this.company.City.State.id}]`,
        order: 'name'
      }).then(res => {
        this.cities = res.result.sort((a, b): any => Number(a.name) - Number(b.name));
      }).catch(err => {
        console.error(err);
        this.loading = false;
      });
    }
  }

  formatCNPJ(value) {
    value = value.replace(/\D/g, '');
    if (value.length > 14) {
      value = value.substring(0, 14);
    }
    return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

}
